import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  AlertTriangle,
  Bell,
  ClipboardCheck,
  FileText,
  Info,
  type LucideIcon,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  ScrollText,
  Shield,
  ShoppingCart,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/lib/i18n';
import { summariseNotification } from '@/lib/notification-summary';
import { logout, useSession } from '@/lib/queries/auth';
import {
  useClearNotifications,
  useNotificationCounts,
  useNotifications,
} from '@/lib/queries/notifications';
import { cn } from '@/lib/utils';

interface NavItem {
  to:
    | '/invoices'
    | '/rules'
    | '/order-confirmations'
    | '/order-confirmations/rules'
    | '/customer-orders'
    | '/customer-orders/rules'
    | '/admin';
  label: string;
  icon: LucideIcon;
}

interface NavSection {
  id: string;
  label: string;
  items: readonly NavItem[];
}

const APP_SHELL_MAX_WIDTH = 'max-w-[98.75rem]';

export function AppShell() {
  const { t } = useI18n();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const isLoginRoute = pathname === '/login';
  const navSections: readonly NavSection[] = [
    {
      id: 'supplier-invoices',
      label: t('shell.section.supplierInvoices'),
      items: [
        { to: '/invoices', label: t('shell.nav.invoices'), icon: FileText },
        { to: '/rules', label: t('shell.nav.rules'), icon: ScrollText },
      ],
    },
    {
      id: 'order-confirmations',
      label: t('shell.section.orderConfirmations'),
      items: [
        {
          to: '/order-confirmations',
          label: t('shell.nav.orderConfirmations'),
          icon: ClipboardCheck,
        },
        { to: '/order-confirmations/rules', label: t('shell.nav.rules'), icon: ScrollText },
      ],
    },
    {
      id: 'customer-orders',
      label: t('shell.section.customerOrders'),
      items: [
        { to: '/customer-orders', label: t('shell.nav.customerOrders'), icon: ShoppingCart },
        { to: '/customer-orders/rules', label: t('shell.nav.rules'), icon: ScrollText },
      ],
    },
  ];
  const adminSection: NavSection = {
    id: 'admin',
    label: t('shell.section.admin'),
    items: [{ to: '/admin', label: t('shell.nav.admin'), icon: Shield }],
  };

  if (isLoginRoute) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <header className="border-b border-border bg-background">
          <div
            className={cn(
              'mx-auto flex h-16 w-full items-center px-4 sm:px-6 lg:px-8',
              APP_SHELL_MAX_WIDTH,
            )}
          >
            <BrandLink to="/login" />
          </div>
        </header>

        <main className="flex-1">
          <div className={cn('mx-auto w-full px-4 py-10 sm:px-6 lg:px-8', APP_SHELL_MAX_WIDTH)}>
            <Outlet />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div
          className={cn(
            'mx-auto flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8',
            APP_SHELL_MAX_WIDTH,
          )}
        >
          <div className="flex min-w-0 items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('shell.mobileMenu')}
              className="md:hidden"
              onClick={() => setMobileSidebarOpen((prev) => !prev)}
            >
              <Menu className="size-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={
                desktopSidebarCollapsed ? t('shell.sidebar.open') : t('shell.sidebar.close')
              }
              className="hidden md:inline-flex"
              onClick={() => setDesktopSidebarCollapsed((prev) => !prev)}
              data-testid="desktop-sidebar-toggle"
            >
              {desktopSidebarCollapsed ? (
                <PanelLeftOpen className="size-5" />
              ) : (
                <PanelLeftClose className="size-5" />
              )}
            </Button>
            <BrandLink to="/invoices" />
          </div>

          <div className="flex items-center gap-2">
            <NotificationsMenu />
            <UserMenuStub />
          </div>
        </div>
      </header>

      {mobileSidebarOpen ? (
        <button
          type="button"
          className="fixed inset-x-0 bottom-0 top-16 z-30 bg-foreground/20 md:hidden"
          aria-label={t('shell.sidebar.close')}
          onClick={() => setMobileSidebarOpen(false)}
        />
      ) : null}

      <aside
        className={cn(
          'fixed bottom-0 left-0 top-16 z-40 w-72 -translate-x-full border-r border-border bg-background transition-transform md:hidden',
          mobileSidebarOpen && 'translate-x-0',
        )}
      >
        <SidebarNav
          sections={navSections}
          adminSection={adminSection}
          collapsed={false}
          onNavigate={() => setMobileSidebarOpen(false)}
          ariaLabel={t('shell.mobileNav')}
        />
      </aside>

      <div className="flex min-h-0 flex-1">
        <aside
          className={cn(
            'sticky top-16 hidden h-[calc(100vh-4rem)] shrink-0 border-r border-border bg-background transition-[width] duration-200 md:flex',
            desktopSidebarCollapsed ? 'w-20' : 'w-72',
          )}
          data-testid="desktop-sidebar"
        >
          <SidebarNav
            sections={navSections}
            adminSection={adminSection}
            collapsed={desktopSidebarCollapsed}
            ariaLabel={t('shell.mainNav')}
          />
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <main className="flex-1">
            <div className={cn('mx-auto w-full px-4 py-10 sm:px-6 lg:px-8', APP_SHELL_MAX_WIDTH)}>
              <Outlet />
            </div>
          </main>

          <footer className="border-t border-border">
            <div
              className={cn(
                'mx-auto flex flex-col items-center justify-between gap-2 px-4 py-6 text-xs text-muted-foreground sm:flex-row sm:px-6 lg:px-8',
                APP_SHELL_MAX_WIDTH,
              )}
            >
              <span>{t('shell.build', { mode: import.meta.env.MODE })}</span>
              <span>
                {t('shell.sourceOfTruth')}{' '}
                <a
                  href="https://github.com/J-ARTS/io.sacai.invoice.automation"
                  className="hover:text-foreground"
                  target="_blank"
                  rel="noreferrer"
                >
                  github.com/J-ARTS/io.sacai.invoice.automation
                </a>
              </span>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}

function SidebarNav({
  sections,
  adminSection,
  collapsed,
  onNavigate,
  ariaLabel,
}: {
  sections: readonly NavSection[];
  adminSection: NavSection;
  collapsed: boolean;
  onNavigate?: (() => void) | undefined;
  ariaLabel: string;
}) {
  return (
    <nav aria-label={ariaLabel} className="flex min-h-0 w-full flex-col">
      <div className="flex-1 overflow-y-auto px-3 py-4">
        {sections.map((section) => (
          <SidebarSection
            key={section.id}
            section={section}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        ))}
      </div>

      <div className="border-t border-border px-3 py-4">
        <SidebarSection
          section={adminSection}
          collapsed={collapsed}
          onNavigate={onNavigate}
          compact
        />
      </div>
    </nav>
  );
}

function SidebarSection({
  section,
  collapsed,
  onNavigate,
  compact = false,
}: {
  section: NavSection;
  collapsed: boolean;
  onNavigate?: (() => void) | undefined;
  compact?: boolean | undefined;
}) {
  return (
    <section className={cn(!compact && 'mb-6 last:mb-0')}>
      <h2
        className={cn(
          'px-3 pb-2 text-[11px] font-semibold uppercase tracking-normal text-muted-foreground',
          collapsed && 'sr-only',
        )}
      >
        {section.label}
      </h2>
      <ul className="flex flex-col gap-1">
        {section.items.map((item) => (
          <li key={item.to}>
            <SidebarLink item={item} collapsed={collapsed} onNavigate={onNavigate} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function SidebarLink({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: (() => void) | undefined;
}) {
  const Icon = item.icon;

  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      title={collapsed ? item.label : undefined}
      activeOptions={{ exact: true }}
      activeProps={{ 'data-status': 'active' }}
      className={cn(
        'flex h-10 items-center gap-3 rounded-md px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
        'data-[status=active]:bg-muted data-[status=active]:text-foreground',
        collapsed && 'justify-center px-0',
      )}
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      <span className={cn('min-w-0 truncate', collapsed && 'sr-only')}>{item.label}</span>
    </Link>
  );
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Notification dropdown keeps loading, empty, preview and action states colocated.
function NotificationsMenu() {
  const { formatDateTime, t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const countsQuery = useNotificationCounts();
  const previewQuery = useNotifications({ limit: 5 }, { enabled: open });
  const clearMutation = useClearNotifications();
  const total = countsQuery.data?.total ?? 0;

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const handleOpenNotificationsPage = () => {
    setOpen(false);
    void navigate({ to: '/notifications' });
  };

  const handleOpenNotification = (invoiceId: string | null) => {
    setOpen(false);
    if (invoiceId) {
      void navigate({ to: '/invoices/$id', params: { id: invoiceId } });
      return;
    }
    void navigate({ to: '/notifications' });
  };

  return (
    <div ref={containerRef} className="relative">
      <Button
        variant="ghost"
        size="icon"
        aria-label={t('shell.notificationsPage')}
        onClick={() => setOpen((prev) => !prev)}
        data-testid="notifications-trigger"
        className="relative"
      >
        <Bell className="size-5" />
        {total > 0 ? (
          <span
            className="absolute -right-1 -top-1 min-w-5 rounded-full bg-destructive px-1 text-center text-[10px] font-semibold leading-5 text-destructive-foreground"
            data-testid="notifications-count"
          >
            {total > 99 ? '99+' : total}
          </span>
        ) : null}
      </Button>

      {open ? (
        <div
          className="absolute right-0 top-11 z-50 flex w-[24rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-md border border-border bg-card shadow-lg"
          data-testid="notifications-dropdown"
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">
                {t('notifications.dropdownTitle')}
              </span>
              {total > 0 ? <Badge variant="outline">{total}</Badge> : null}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void clearMutation.mutateAsync();
              }}
              disabled={clearMutation.isPending || total === 0}
              data-testid="notifications-clear"
            >
              {clearMutation.isPending ? t('notifications.clearing') : t('notifications.clearAll')}
            </Button>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {previewQuery.isPending ? (
              <div className="px-4 py-6 text-sm text-muted-foreground">
                {t('notifications.loading')}
              </div>
            ) : previewQuery.isError ? (
              <div className="px-4 py-6 text-sm text-destructive">{previewQuery.error.message}</div>
            ) : previewQuery.data.items.length === 0 ? (
              <div className="px-4 py-6 text-sm text-muted-foreground">
                {t('notifications.dropdownEmpty')}
              </div>
            ) : (
              <div className="divide-y divide-border">
                {/* biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Notification preview item rendering keeps summary, link and unread state together. */}
                {previewQuery.data.items.map((item) => {
                  const summary = summariseNotification(item, t);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-muted/50"
                      onClick={() => handleOpenNotification(summary.invoiceId)}
                      data-testid={`notification-preview-${item.id}`}
                    >
                      <span className="mt-0.5 shrink-0" aria-hidden>
                        {summary.level === 'error' ? (
                          <AlertTriangle className="size-4 text-destructive" />
                        ) : summary.level === 'warning' ? (
                          <AlertTriangle className="size-4 text-amber-600" />
                        ) : (
                          <Info className="size-4 text-sky-600" />
                        )}
                      </span>

                      <span className="flex min-w-0 flex-1 flex-col gap-1">
                        <span className="line-clamp-1 text-sm font-medium text-foreground">
                          {summary.title}
                        </span>
                        <span className="line-clamp-2 text-xs text-muted-foreground">
                          {summary.message}
                        </span>
                        <span className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                          <span>{formatDateTime(item.createdAt)}</span>
                          {item.customerName ? <span>{item.customerName}</span> : null}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between border-t border-border px-4 py-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleOpenNotificationsPage}
              data-testid="notifications-view-all"
            >
              {t('notifications.viewAll')}
            </Button>
            {clearMutation.isError ? (
              <span className="text-xs text-destructive">{clearMutation.error.message}</span>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function BrandLink({ to }: { to: '/invoices' | '/login' }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-2 text-base font-semibold tracking-tight text-foreground"
    >
      <span
        aria-hidden
        className="grid size-7 place-items-center rounded-md bg-primary text-primary-foreground"
      >
        <FileText className="size-4" />
      </span>
      Invoice Automation
    </Link>
  );
}

function UserMenuStub() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const sessionQuery = useSession();
  const [open, setOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const user = sessionQuery.data?.user;
  const initials = getUserInitials(user?.name ?? null, user?.email ?? null);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
      void navigate({ to: '/login', replace: true });
    } finally {
      setLoggingOut(false);
      setOpen(false);
    }
  };

  const handleOpenSettings = () => {
    setOpen(false);
    void navigate({ to: '/settings' });
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        className="grid size-9 place-items-center rounded-full bg-muted text-sm font-medium text-foreground hover:bg-secondary"
        aria-label={t('shell.userMenu')}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="user-menu-trigger"
        onClick={() => setOpen((prev) => !prev)}
      >
        {initials}
      </button>

      {open ? (
        <div
          role="menu"
          aria-label={t('shell.userMenuOptions')}
          className="absolute right-0 top-11 z-50 min-w-40 rounded-md border border-border bg-card p-1 shadow-lg"
          data-testid="user-menu-dropdown"
        >
          <button
            type="button"
            role="menuitem"
            className="flex w-full rounded-sm px-3 py-2 text-left text-sm text-foreground hover:bg-muted"
            onClick={handleOpenSettings}
            data-testid="user-menu-settings"
          >
            {t('shell.settings')}
          </button>
          <button
            type="button"
            role="menuitem"
            className="flex w-full rounded-sm px-3 py-2 text-left text-sm text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => {
              void handleLogout();
            }}
            disabled={loggingOut}
            data-testid="user-menu-logout"
          >
            {loggingOut ? t('shell.logoutPending') : t('shell.logout')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function getUserInitials(name: string | null, email: string | null): string {
  const trimmedName = name?.trim() ?? '';
  if (trimmedName.length > 0) {
    const parts = trimmedName.split(/\s+/).filter((part) => part.length > 0);
    const initials = parts
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
    if (initials.length > 0) return initials;
  }

  const localPart = email?.split('@')[0]?.trim() ?? '';
  if (localPart.length > 0) {
    const compact = localPart.replace(/[^a-zA-Z0-9]/g, '');
    if (compact.length >= 2) return compact.slice(0, 2).toUpperCase();
    if (compact.length === 1) return compact.toUpperCase();
  }

  return '?';
}
